import { Header } from "../../common/Header";
import { Button } from "../../ui/button";
import { Heart } from "lucide-react";
import { ProductCard } from "../../Product/ProductCard";
import { useFavorites, useNotificationHelpers } from "@nusrif/hooks";
import { useNavigate } from "react-router";
import {
  getLowestPriceVariant,
  getUser,
  isProductInStock,
  removeAllUsersFavorite,
  removeFavorite,
} from "@nusrif/services";
import { formatCurrency } from "@nusrif/utils";
import config from "@nusrif/config";

export const MyFavouritesPage = () => {
  const navigate = useNavigate();
  const { favorites, isLoading, error, refetch } = useFavorites();
  const { showSuccess } = useNotificationHelpers();

  const handleFavoriteClick = (productId: number) => {
    console.log(`Favorite clicked for product ${productId}`);
    // Add your favorite logic here
  };

  const handleProductClick = (productId: number) => {
    console.log(`Product clicked: ${productId}`);
    navigate(`/product/${productId}`);
  };

  const handleClearAll = async () => {
    const userId = getUser()?.id;
    if (!userId) return;

    showSuccess("Clear all favorites");

    await removeAllUsersFavorite(userId);
    refetch();
  };

  const handleRemoveFavorite = async (productId: number) => {
    const userId = getUser()?.id;
    if (!userId) return;

    showSuccess("Removed from favourites");

    await removeFavorite(userId, productId);
    refetch();
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-gray-50">
        <Header
          title="My Favourites"
          showBackButton={true}
          showCartButton={false}
        />
        <div className="pt-20 px-5 space-y-6">
          <div className="flex items-center justify-center">
            <p className="text-gray-600">Loading favorites...</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="w-full h-[250px] bg-gray-200 rounded-lg mb-3"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen w-full bg-gray-50">
        <Header
          title="My Favourites"
          showBackButton={true}
          showCartButton={false}
        />
        <div className="pt-20 px-5 space-y-6">
          <div className="text-center py-8">
            <p className="text-gray-500">Failed to load favorites</p>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <Header
        title="My Favourites"
        showBackButton={true}
        showCartButton={false}
      />

      {/* Main Content */}
      <div className="pt-20 px-5 space-y-6">
        {favorites.length > 0 ? (
          <>
            {/* Header with Clear All */}
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                {favorites.length} item{favorites.length !== 1 ? "s" : ""} in
                your favourites
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            </div>
            {/* Favourites Grid */}
            <div className="grid grid-cols-2 gap-4">
              {favorites.map((favorite) => {
                const product = favorite.product;
                const lowestPriceVariant = getLowestPriceVariant(product);
                const price = lowestPriceVariant
                  ? formatCurrency(
                      lowestPriceVariant.priceWithTax,
                      lowestPriceVariant.currencyCode
                    )
                  : formatCurrency(0, config.region.currencyCode);
                const mrpPrice = product.customFields?.mrpPrice
                  ? formatCurrency(
                      product.customFields.mrpPrice,
                      config.region.currencyCode
                    )
                  : formatCurrency(0, config.region.currencyCode);

                const image =
                  product.featuredAsset?.preview ||
                  product.assets[0]?.preview ||
                  "/image.png";
                const brand =
                  product.facetValues.find((f: any) => f.facet.name === "Brand")
                    ?.name || "Two & Five";
                const name = product.name;
                const isInStock = isProductInStock(product);
                const productId = parseInt(product.id) || 0;

                return (
                  <ProductCard
                    key={favorite.productId}
                    id={productId}
                    image={image}
                    brand={brand}
                    name={name}
                    price={price}
                    mrpPrice={mrpPrice}
                    heartIcon={isInStock ? "/love.png" : "/love-1.png"}
                    removeButton={true}
                    onRemove={handleRemoveFavorite}
                    onFavoriteClick={handleFavoriteClick}
                    onProductClick={handleProductClick}
                  />
                );
              })}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-black mb-2">
              No Favourites Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start adding items to your favourites to see them here
            </p>
            <Button
              onClick={() => navigate("/store")}
              className="px-8 py-3 rounded-full font-semibold"
            >
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
